/**
 * LearnerHomework — Turn 1 of Phase 1 learner-side completion.
 *
 * Top-of-app destination at /learn/homework.
 * Splits the existing homework_assignments + reflection-prompt flows into
 * the owner-required Pre Homework / Post Homework structure.
 *
 *   Pre Homework:  what to prepare / understand / focus on BEFORE the
 *                  next lesson — pulls from /api/learner-framework/me/homework
 *                  (existing assignments / stage-unlock recommendations).
 *
 *   Post Homework: reflection on the most recent completed lesson —
 *                  pulls from /api/after-lesson-reflection/me (canonical,
 *                  the legacy /api/reflections/learner/pending was
 *                  retired in §23 Step 7). The card here is a calm
 *                  "open on home" pointer; the actual reflection UI
 *                  lives in <AfterLessonReflectionCard>.
 *
 * Tone (locked):
 *   - clarity without blame
 *   - progress without shame
 *   - positive next action
 *   - confidence through proof
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, BookOpen, MessageCircle, Sparkles,
  CheckCircle2, ArrowRight, X, Loader2, Lightbulb,
} from "lucide-react";
import { DarkModeToggle } from "../components/DarkModeToggle";
import CalmEmptyState from "../components/learner/CalmEmptyState";
import LearnerPendingUploadsPrompt from "./LearnerPendingUploadsPrompt";
import AfterLessonReflectionCard from "../components/learner/AfterLessonReflectionCard";

const API = process.env.REACT_APP_BACKEND_URL;

const SOURCE_LABEL = {
  stage_unlock:   "Stage unlocked",
  foundation_gap: "Foundation refresher",
  manual:         "From your instructor",
  operational_learning_state: "From your last lesson",
  evidence_homework: "Prepared from your evidence",
};

const OUTCOME_LABEL = {
  understood: "You understand it",
  practised: "You tried it",
  need_more_help: "You asked for more help",
  not_confident: "You're still unsure",
};

export default function LearnerHomework() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [pendingReflection, setPendingReflection] = useState(null);
  const [learningNextBest, setLearningNextBest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const actionInFlight = useRef(false);
  const [notes, setNotes] = useState({});
  const [homeworkError, setHomeworkError] = useState(false);
  const [reflectionError, setReflectionError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setHomeworkError(false);
    setReflectionError(false);
    try {
      const [hwResp, rxResp] = await Promise.allSettled([
        axios.get(`${API}/api/learner-framework/me/homework`, { withCredentials: true }),
        axios.get(`${API}/api/after-lesson-reflection/me`, { withCredentials: true }),
      ]);
      if (hwResp.status === "fulfilled") {
        setItems(hwResp.value.data?.items || []);
        setLearningNextBest(hwResp.value.data?.learning_next_best || null);
      } else {
        setHomeworkError(true);
        setLearningNextBest(null);
      }
      // Canonical reflection endpoint returns {prompt: {...}} or
      // {prompt: null}. We surface the same "Post Homework" card
      // when a prompt is pending — but the calm UI itself lives
      // in <AfterLessonReflectionCard> on the home, so we only
      // need the boolean here.
      if (rxResp.status === "fulfilled") {
        const prompt = rxResp.value.data?.prompt || null;
        setPendingReflection(prompt ? { signal_key: prompt.signal_key } : null);
      } else {
        setReflectionError(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleComplete(id, outcome = "understood") {
    if (actionInFlight.current) return;
    actionInFlight.current = true;
    setBusyId(id);
    try {
      const { data } = await axios.post(
        `${API}/api/learner-framework/me/homework/${id}/complete`,
        { outcome, ...(notes[id]?.trim() ? { note: notes[id].trim() } : {}) },
        { withCredentials: true },
      );
      if (data?.success !== true || !data.completed_at || data.outcome !== outcome) {
        throw new Error("Your response could not be confirmed as saved. Your words are still here; retry to check.");
      }
      const messages = {
        understood: "Understood — your instructor will see that.",
        practised: "Practice recorded — your instructor will see that.",
        need_more_help: "Help request recorded for your instructor.",
        not_confident: "Your uncertainty is recorded for your instructor to review.",
      };
      const sharing = data?.shared_learning_sync;
      toast.success(sharing === "recorded"
        ? (messages[outcome] || "Your response was recorded.")
        : sharing === "pending"
          ? "Your response is saved. Sharing with your instructor is still pending."
          : "Your response is saved. We couldn't confirm whether it has reached your instructor.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Couldn't update — try again.");
    } finally {
      actionInFlight.current = false;
      setBusyId(null);
    }
  }

  async function handleDismiss(id) {
    if (actionInFlight.current) return;
    actionInFlight.current = true;
    setBusyId(id);
    try {
      await axios.post(
        `${API}/api/learner-framework/me/homework/${id}/dismiss`, {},
        { withCredentials: true },
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update — try again.");
    } finally {
      actionInFlight.current = false;
      setBusyId(null);
    }
  }

  const recommended = items.filter((i) => i.recommended && !i.completed_at);
  const everythingElse = items.filter((i) => !i.recommended || i.completed_at);

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-12">
      {/* ─── Header ─────────────────────────────────── */}
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-4 py-3 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <button
            data-testid="back-to-learn"
            onClick={() => nav("/learn")}
            className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <ArrowLeft className="w-5 h-5 text-slate-500" />
          </button>
          <h1 className="text-lg font-extrabold text-slate-900 dark:text-white flex-1">
            Homework
          </h1>
          <DarkModeToggle />
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 mt-5 space-y-7">
        {/* ─── Pending paid clip uploads ───────────────────────
              Phase Pl-D (Feb 7 2026) — moved here from /learn so that
              the prompt sits in the same surface where pupils think
              about homework. Hides itself when there's nothing to
              upload, so it's invisible most of the time. */}
        <LearnerPendingUploadsPrompt />

        {/* Top-line: the loop */}
        <p data-testid="homework-loop" className="text-[12px] text-slate-500 dark:text-slate-400 leading-relaxed text-center">
          Prepare → Drive → Reflect → Progress
        </p>

        {learningNextBest && (
          <section
            data-testid="learner-next-best"
            className="rounded-2xl border border-violet-200 bg-violet-50 p-4 text-violet-950 dark:border-violet-800 dark:bg-violet-950/30 dark:text-violet-100"
          >
            <p className="text-[10px] font-extrabold uppercase tracking-widest text-violet-700 dark:text-violet-300">
              Your next best step
            </p>
            <p className="mt-2 text-sm font-semibold">{learningNextBest.pupil_next_step}</p>
            <p className="mt-3 text-sm">Think about: {learningNextBest.next_question}</p>
            <a
              href={learningNextBest.understanding?.deep_link || "/learn"}
              className="mt-3 inline-flex min-h-11 items-center rounded-xl bg-violet-700 px-4 text-sm font-extrabold text-white"
            >
              Open the understanding
            </a>
          </section>
        )}

        {/* ─── PRE HOMEWORK ─────────────────────────── */}
        <section data-testid="pre-homework-section">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center">
              <Lightbulb className="w-4 h-4" strokeWidth={2.5} />
            </div>
            <h2 className="text-xl font-extrabold text-slate-900 dark:text-white">
              Pre Homework
            </h2>
          </div>
          <p className="text-[13px] text-slate-500 dark:text-slate-400 mb-3 leading-relaxed">
            Before your next lesson — what to read, prepare, and focus on.
          </p>
          <button
            type="button"
            data-testid="ask-about-homework"
            onClick={() => nav("/learn/learning")}
            className="mb-4 inline-flex min-h-11 items-center rounded-xl border border-slate-300 bg-white px-4 text-sm font-extrabold text-slate-800 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
          >
            Ask about this preparation
          </button>

          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
            </div>
          ) : homeworkError ? (
            <div role="alert" data-testid="homework-load-error" className="rounded-2xl border border-slate-200 p-4 text-sm text-slate-700 dark:text-slate-200">
              <p>We couldn't load your homework just now.</p>
              <button type="button" data-testid="homework-retry" onClick={load} className="mt-2 min-h-11 rounded-xl border border-slate-300 px-4 font-bold">Try again</button>
            </div>
          ) : items.length === 0 ? (
            <CalmEmptyState
              testid="pre-homework-empty"
              tone="nothing-yet"
              title="Nothing to prepare right now"
              body="Your instructor will set the next focus on your next lesson."
            />
          ) : (
            <div className="space-y-3">
              {recommended.length > 0 && (
                <div>
                  <p className="text-[10px] font-extrabold uppercase tracking-widest text-emerald-700 dark:text-emerald-300 mb-2 flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3" /> Recommended next
                  </p>
                  <div className="space-y-2">
                    {recommended.map((it) => (
                      <HomeworkRow
                        key={it.id}
                        item={it}
                        note={notes[it.id] || ""}
                        onNote={value => setNotes(current => ({ ...current, [it.id]: value }))}
                        busy={busyId !== null}
                        onComplete={handleComplete}
                        onDismiss={handleDismiss}
                      />
                    ))}
                  </div>
                </div>
              )}
              {everythingElse.length > 0 && (
                <div>
                  {recommended.length > 0 && (
                    <p className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-2 mt-4">
                      Also assigned
                    </p>
                  )}
                  <div className="space-y-2">
                    {everythingElse.map((it) => (
                      <HomeworkRow
                        key={it.id}
                        item={it}
                        note={notes[it.id] || ""}
                        onNote={value => setNotes(current => ({ ...current, [it.id]: value }))}
                        busy={busyId !== null}
                        onComplete={handleComplete}
                        onDismiss={handleDismiss}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* ─── POST HOMEWORK ────────────────────────── */}
        <section data-testid="post-homework-section">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center">
              <MessageCircle className="w-4 h-4" strokeWidth={2.5} />
            </div>
            <h2 className="text-xl font-extrabold text-slate-900 dark:text-white">
              Post Homework
            </h2>
          </div>
          <p className="text-[13px] text-slate-500 dark:text-slate-400 mb-4 leading-relaxed">
            After your last lesson — reflect on what landed, what still needs clarity, and what you take forward.
          </p>

          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
            </div>
          ) : reflectionError ? (
            <div role="alert" data-testid="reflection-load-error" className="rounded-2xl border border-slate-200 p-4 text-sm text-slate-700 dark:text-slate-200">
              <p>We couldn't check for reflections just now.</p>
              <button type="button" data-testid="reflection-retry" onClick={load} className="mt-2 min-h-11 rounded-xl border border-slate-300 px-4 font-bold">Try again</button>
            </div>
          ) : pendingReflection ? (
            <div
              data-testid="post-homework-reflection-card"
              className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-5"
            >
              <p className="text-[10px] font-extrabold uppercase tracking-widest text-amber-600">
                Ready when you are
              </p>
              <h3 className="text-base font-extrabold text-slate-900 dark:text-white mt-1">
                How did your lesson go?
              </h3>
              <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-1.5 mb-3 leading-relaxed">
                One question, one moment. Your answer helps prepare the next lesson.
              </p>
              <AfterLessonReflectionCard />
            </div>
          ) : (
            <CalmEmptyState
              testid="post-homework-empty"
              tone="nothing-yet"
              title="No reflections waiting"
              body="After your next lesson, this is where you'll come to reflect on it."
            />
          )}
        </section>
      </div>
    </div>
  );
}

/* ── Small row component (mirrors LearnerHomeworkList row) ── */
function HomeworkRow({ item, busy, onComplete, onDismiss, note, onNote }) {
  const sourceLabel = item.source === "evidence_homework" && !item.source_evidence_ids?.length
    ? "Preparation to explore together"
    : SOURCE_LABEL[item.source] || "Homework";
  return (
    <div
      data-testid={`pre-homework-row-${item.id}`}
      className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3.5 flex items-start gap-3"
    >
      <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center flex-none">
        <BookOpen className="w-5 h-5 text-slate-600 dark:text-slate-300" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-0.5">
          {sourceLabel}
        </p>
        <p className="text-sm font-extrabold text-slate-900 dark:text-white truncate">
          {item.lesson_title || "Lesson"}
        </p>
        {item.section_title && (
          <p className="text-[12px] text-slate-500 dark:text-slate-400 truncate">
            {item.section_title}
          </p>
        )}
        {item.reason && (
          <p className="mt-2 text-[12px] leading-relaxed text-slate-700 dark:text-slate-200">
            {item.reason}
          </p>
        )}
        {item.homework_preparation?.length > 0 && (
          <ul className="mt-2 list-disc pl-4 text-[12px] leading-relaxed text-slate-700 dark:text-slate-200">
            {item.homework_preparation.map((step, index) => <li key={index}>{step}</li>)}
          </ul>
        )}
        {item.suggested_practice && (
          <p className="mt-2 text-[12px] leading-relaxed text-slate-700 dark:text-slate-200">
            {item.suggested_practice}
          </p>
        )}
        {item.review_prompts?.length > 0 && (
          <div className="mt-3 text-[12px] text-slate-700 dark:text-slate-200">
            <p className="font-bold">Questions to think about</p>
            <ul className="mt-1 list-disc pl-4">
              {item.review_prompts.map((prompt, index) => <li key={index}>{prompt}</li>)}
            </ul>
          </div>
        )}
        {item.success_indicators?.length > 0 && (
          <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
            What to look for: {item.success_indicators.join(" · ")}
          </p>
        )}
        {item.evidence_limitations?.length > 0 && (
          <div className="mt-3 text-[12px] text-slate-600 dark:text-slate-300">
            <p className="font-bold">About this preparation</p>
            <ul className="mt-1 list-disc pl-4">
              {item.evidence_limitations.map((limitation, index) => <li key={index}>{limitation}</li>)}
            </ul>
          </div>
        )}
        {item.instructor_confirmation_required && (
          <p className="mt-2 text-[12px] text-slate-600 dark:text-slate-300">Your instructor will check this with you during a lesson.</p>
        )}
        <div className="flex flex-wrap gap-2 mt-2.5">
          {item.content_lesson_id && (
            <Link
              data-testid={`open-lesson-${item.id}`}
              to={`/lesson/${item.content_lesson_id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-900 text-white text-[11px] font-extrabold active:scale-95"
            >
              Open lesson <ArrowRight className="w-3 h-3" />
            </Link>
          )}
          {item.completed_at ? (
            <div className="text-[12px] text-slate-700 dark:text-slate-200">
              <p className="font-bold">Response saved: {OUTCOME_LABEL[item.learner_outcome] || "Completed"}</p>
              {item.learner_note && <p className="mt-1 whitespace-pre-wrap">{item.learner_note}</p>}
            </div>
          ) : (
            <>
              <label className="w-full text-[12px] text-slate-700 dark:text-slate-200">
                Tell your instructor what felt clear or uncertain (optional)
                <textarea
                  data-testid={`homework-note-${item.id}`}
                  value={note}
                  onChange={event => onNote(event.target.value)}
                  disabled={busy}
                  maxLength={500}
                  rows={3}
                  className="mt-1 w-full rounded-xl border border-slate-300 bg-white p-3 text-base dark:border-slate-600 dark:bg-slate-900"
                />
              </label>
              <p className="w-full text-[11px] font-bold text-slate-600 dark:text-slate-300">
                How did this preparation land?
              </p>
              <button
                data-testid={`homework-understood-${item.id}`}
                onClick={() => onComplete(item.id, "understood")}
                disabled={busy}
                className="inline-flex min-h-10 items-center gap-1 px-3 py-1.5 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-[11px] font-extrabold disabled:opacity-50"
              >
                <CheckCircle2 className="w-3.5 h-3.5" /> I understand it
              </button>
              <button
                data-testid={`homework-practised-${item.id}`}
                onClick={() => onComplete(item.id, "practised")}
                disabled={busy}
                className="inline-flex min-h-10 items-center gap-1 px-3 py-1.5 rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-700 text-[11px] font-extrabold disabled:opacity-50"
              >
                I tried it
              </button>
              <button
                data-testid={`homework-help-${item.id}`}
                onClick={() => onComplete(item.id, "need_more_help")}
                disabled={busy}
                className="inline-flex min-h-10 items-center gap-1 px-3 py-1.5 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-700 text-[11px] font-extrabold disabled:opacity-50"
              >
                I need more help
              </button>
              <button
                data-testid={`homework-unsure-${item.id}`}
                onClick={() => onComplete(item.id, "not_confident")}
                disabled={busy}
                className="inline-flex min-h-10 items-center gap-1 px-3 py-1.5 rounded-xl bg-violet-50 hover:bg-violet-100 text-violet-700 text-[11px] font-extrabold disabled:opacity-50"
              >
                I'm still unsure
              </button>
              <button
                data-testid={`dismiss-${item.id}`}
                onClick={() => onDismiss(item.id)}
                disabled={busy}
                aria-label="Not now"
                className="inline-flex min-h-10 items-center gap-1 px-2.5 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 text-[11px] font-bold disabled:opacity-50"
              >
                <X className="w-3.5 h-3.5" /> Not now
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

